import React, { useEffect, useMemo, useRef, useState } from "react";
import { Upload, Download, Plus, X, Copy, Wand2, FolderOpen, ChevronRight, ChevronDown, Trash2, HelpCircle } from "lucide-react";
import { Helmet, HelmetProvider } from "react-helmet-async";

// --- Tiny helpers -----------------------------------------------------------
const isObj = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const isArr = Array.isArray;

// Decode base64url to UTF-8 string (for deep links)
function decodeBase64Url(s) {
  try {
    if (!s) return null;
    const pad = s.length % 4 ? s + "=".repeat(4 - (s.length % 4)) : s;
    const b64 = pad.replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64);
    const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
    const decoder = new TextDecoder();
    return decoder.decode(bytes);
  } catch (_) {
    return null;
  }
}

function tryParseJSON(text) {
  try {
    return [JSON.parse(text), null];
  } catch (e) {
    return [null, e.message];
  }
}

// Recursively collect dot-paths and array-paths for suggestions
function collectPaths(value, base = "", paths = new Set(), arrayPaths = new Set(), depth = 0) {
  if (depth > 8) return { paths, arrayPaths }; // keep things snappy
  if (isArr(value)) {
    if (base) arrayPaths.add(base);
    const limit = Math.min(value.length, 3);
    for (let i = 0; i < limit; i++) collectPaths(value[i], base, paths, arrayPaths, depth + 1);
    return { paths, arrayPaths };
  }
  if (isObj(value)) {
    const keys = Object.keys(value).slice(0, 50);
    for (const k of keys) {
      const next = base ? `${base}.${k}` : k;
      paths.add(next);
      collectPaths(value[k], next, paths, arrayPaths, depth + 1);
      if (isArr(value[k])) arrayPaths.add(next);
    }
    return { paths, arrayPaths };
  }
  // primitive leaf
  if (base) paths.add(base);
  return { paths, arrayPaths };
}

// Get array by dot path (for the "items path" you iterate over)
function getArrayByPath(data, path) {
  if (!path) return isArr(data) ? data : [];
  const segs = path.split(".").filter(Boolean);
  let cur = data;
  for (const s of segs) {
    if (cur == null) return [];
    if (isArr(cur)) {
      // flatten over all children at this segment
      cur = cur
        .flatMap((item) => (isObj(item) || isArr(item) ? item[s] : undefined))
        .filter((v) => v !== undefined);
    } else if (isObj(cur)) {
      cur = cur[s];
    } else {
      return [];
    }
  }
  return isArr(cur) ? cur : [];
}

// Flexible extractor that flattens arrays encountered along the path
function getValuesAtPath(value, path) {
  if (!path) return [value];
  const segs = path.split(".").filter(Boolean);
  const walk = (val, idx) => {
    if (idx === segs.length) return [val];
    const key = segs[idx];
    if (val == null) return [];
    if (isArr(val)) return val.flatMap((v) => walk(v, idx)); // do not consume segment on arrays; keep searching inside each element
    if (isObj(val)) return walk(val[key], idx + 1);
    return []; // cannot descend
  };
  return walk(value, 0);
}

function stringifyShort(v, max = 160) {
  let s;
  if (typeof v === "string") s = v;
  else if (typeof v === "number" || typeof v === "boolean") s = String(v);
  else if (v == null) s = "";
  else s = JSON.stringify(v);
  if (s.length > max) s = s.slice(0, max - 1) + "…";
  return s;
}

function download(filename, text) {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function toCSV(rows, headers) {
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    if (/[,"\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
    return s;
  };
  const lines = [];
  lines.push(["#", ...headers].map(esc).join(","));
  for (const r of rows) {
    lines.push([r.idx, ...headers.map((h) => esc(r.values[h]))].join(","));
  }
  return lines.join("\n");
}

// --- Compact JSON tree ------------------------------------------------------
const TreeNode = ({ k, v, level = 0, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  // Keep local open state in sync when defaultOpen changes (Expand/Collapse all)
  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  const indent = { paddingLeft: `${level * 16}px` };

  const keyLabel = k !== undefined ? (
    <span className="font-mono text-[13px] text-slate-500 dark:text-slate-400 mr-1">{k}: </span>
  ) : null;

  const formatLeaf = (val) => {
    let text;
    let cls = "text-slate-800 dark:text-slate-200";
    if (val == null) {
      text = "null";
      cls = "text-slate-400 dark:text-slate-500 italic";
    } else if (typeof val === "string") {
      text = JSON.stringify(val);
      cls = "text-emerald-700 dark:text-emerald-300";
    } else if (typeof val === "number") {
      text = String(val);
      cls = "text-blue-700 dark:text-blue-300";
    } else if (typeof val === "boolean") {
      text = String(val);
      cls = "text-indigo-700 dark:text-indigo-300";
    } else {
      text = JSON.stringify(val);
    }
    const max = 160;
    if (text.length > max) text = text.slice(0, max - 1) + "…";
    return { text, cls };
  };

  if (isArr(v)) {
    return (
      <div className="leading-7" style={indent}>
        <button onClick={() => setOpen((s) => !s)} className="inline-flex items-center gap-1 text-slate-800 dark:text-slate-200">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {keyLabel}
          <span className="font-mono text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">[Array {v.length}]</span>
        </button>
        {open && (
          <div className="mt-1">
            {v.slice(0, 200).map((child, i) => (
              <TreeNode key={i} k={i} v={child} level={level + 1} defaultOpen={defaultOpen} />
            ))}
            {v.length > 200 && (
              <div className="text-xs text-slate-400" style={{ paddingLeft: `${(level + 1) * 14}px` }}>…truncated</div>
            )}
          </div>
        )}
      </div>
    );
  }
  if (isObj(v)) {
    const keys = Object.keys(v);
    return (
      <div className="leading-7" style={indent}>
        <button onClick={() => setOpen((s) => !s)} className="inline-flex items-center gap-1 text-slate-800 dark:text-slate-200">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          {keyLabel}
          <span className="font-mono text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{`{Object ${keys.length}}`}</span>
        </button>
        {open && (
          <div className="mt-1">
            {keys.map((kk) => (
              <TreeNode key={kk} k={kk} v={v[kk]} level={level + 1} defaultOpen={defaultOpen} />
            ))}
          </div>
        )}
      </div>
    );
  }
  const { text, cls } = formatLeaf(v);
  return (
    <div className="leading-7" style={indent}>
      {keyLabel}
      <span className={`font-mono text-[13px] break-words ${cls}`}>{text}</span>
    </div>
  );
};

// --- Tag input --------------------------------------------------------------
function TagsInput({ suggestions = [], value, onChange, placeholder = "Add path…" }) {
  const [input, setInput] = useState("");
  const inputRef = useRef(null);
  const suggs = useMemo(() => {
    if (!input) return suggestions.slice(0, 50);
    const q = input.toLowerCase();
    return suggestions.filter((s) => s.toLowerCase().includes(q)).slice(0, 50);
  }, [input, suggestions]);

  const add = (t) => {
    const trimmed = (t || input).trim();
    if (!trimmed) return;
    if (!value.includes(trimmed)) onChange([...value, trimmed]);
    setInput("");
    inputRef.current?.focus();
  };
  const remove = (t) => onChange(value.filter((x) => x !== t));

  return (
    <div className="w-full">
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200 dark:border-slate-700 p-2 bg-white/70 dark:bg-slate-900/70">
        {value.map((t) => (
          <span key={t} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-xs">
            {t}
            <button onClick={() => remove(t)} className="hover:text-rose-600" aria-label={`Remove ${t}`}>
              <X size={14} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              add();
            }
            if (e.key === "Backspace" && !input && value.length) onChange(value.slice(0, -1));
          }}
          placeholder={placeholder}
          className="flex-1 min-w-[10ch] bg-transparent outline-none text-sm text-slate-800 dark:text-slate-200"
        />
        <button onClick={() => add()} className="ml-auto inline-flex items-center gap-1 text-sm px-2 py-1 rounded-xl bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
          <Plus size={14} /> Add
        </button>
      </div>
      {suggs.length > 0 && (
        <div className="mt-1 max-h-48 overflow-auto rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm">
          {suggs.map((s) => (
            <button key={s} onClick={() => add(s)} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-800/60">
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Self-tests (run once, logs to console; harmless in prod) --------------
function runSelfTests() {
  try {
    const sample = {
      places: [
        { displayName: { text: "A" }, rating: 4 },
        { displayName: { text: "B" }, rating: 5 },
      ],
    };
    const arr = getArrayByPath(sample, "places");
    if (!isArr(arr) || arr.length !== 2) throw new Error("getArrayByPath failed");

    const v1 = getValuesAtPath(sample.places[0], "displayName.text");
    if (!(v1.length === 1 && v1[0] === "A")) throw new Error("getValuesAtPath simple path failed");

    const nested = { a: [{ b: { c: 1 } }, { b: { c: 2 } }] };
    const v2 = getValuesAtPath(nested, "a.b.c");
    if (!(v2.length === 2 && v2[0] === 1 && v2[1] === 2)) throw new Error("getValuesAtPath flatten failed");

    // Suggestion collection smoke test
    const { paths, arrayPaths } = collectPaths(sample);
    if (!paths.size || !arrayPaths.size) throw new Error("collectPaths failed");

    // CSV formatting
    const rows = [
      { idx: 0, values: { name: 'A, B', x: '"quoted"' } },
      { idx: 1, values: { name: 'C', x: 'D' } },
    ];
    const csv = toCSV(rows, ["name", "x"]);
    if (!/\n1,C,D$/.test(csv.trim())) throw new Error("toCSV formatting failed");

    // eslint-disable-next-line no-console
    console.log("JSON Field Explorer self-tests: ✅ Passed");
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("JSON Field Explorer self-tests: ❌ Failed:", err);
  }
}
runSelfTests();

// --- Main App ---------------------------------------------------------------
export default function JSONFieldExplorer() {
  // --- Lightweight local cache (browser-only) ---
  const LS_KEYS = {
    RAW: "jfe:raw:v1",
    ITEMS: "jfe:itemsPath:v1",
    FIELDS: "jfe:fields:v1",
  };
  const MAX_CACHE_CHARS = 250_000; // ~250 KB budget
  const readLS = (k) => {
    try {
      if (typeof window === "undefined") return null;
      return window.localStorage.getItem(k);
    } catch (_) {
      return null;
    }
  };
  const writeLS = (k, v) => {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.setItem(k, v);
    } catch (_) {}
  };
  const removeLS = (k) => {
    try {
      if (typeof window === "undefined") return;
      window.localStorage.removeItem(k);
    } catch (_) {}
  };
  // --- SEO constants ---
  const canonical =
    typeof window !== "undefined" && window.location
      ? window.location.origin + window.location.pathname
      : "https://jsonfieldexplorer.com";
  const SEO = {
    title: "JSON Field Explorer — Free Online JSON Viewer & JSON Path Extractor",
    description:
      "Extract fields from JSON online: load JSON, choose an items array, select dot paths (e.g., name.text), preview values per item, and export CSV. Free, fast, privacy-first JSON viewer & JSON path extractor.",
    image: "/og.png",
    url: canonical,
    keywords:
      "json viewer, json path, json path extractor, json path tester, json path finder, extract fields from json online, json to csv, json field picker, json explorer, parse json, jsonpath alternative, flatten json to csv",
  };

  const defaultSample = {
    places: [
      {
        displayName: { text: "SF Dry Dock", languageCode: "en" },
        formattedAddress: "404-448 20th St, San Francisco, CA 94107, USA",
      },
      {
        displayName: { text: "Impark Lot 85", languageCode: "en" },
        formattedAddress: "816 Michigan St, San Francisco, CA 94107, USA",
      },
    ],
  };

  // Seed from cache when available
  // Allow deep-linking with URL parameters: ?j=<base64url JSON>&i=<itemsPath>&f=<field1,field2>
  const searchParams =
    typeof window !== "undefined" && window.location
      ? new URLSearchParams(window.location.search)
      : null;
  const urlRaw = (() => {
    if (!searchParams) return null;
    const j = searchParams.get("j");
    if (j) return decodeBase64Url(j);
    return null;
  })();
  const urlItems = searchParams?.get("i") || null;
  const urlFields = (() => {
    const f = searchParams?.get("f");
    if (!f) return null;
    const arr = f.split(",").map((s) => s.trim()).filter(Boolean);
    return arr.length ? arr : null;
  })();

  const [raw, setRaw] = useState(() => {
    if (urlRaw) return urlRaw;
    const cached = readLS(LS_KEYS.RAW);
    return cached ?? JSON.stringify(defaultSample, null, 2);
  });
  const [[data, parseErr], setData] = useState([defaultSample, null]);
  const [itemsPath, setItemsPath] = useState(() => {
    if (urlItems != null) return urlItems;
    const hasRaw = readLS(LS_KEYS.RAW) != null;
    if (hasRaw) {
      const v = readLS(LS_KEYS.ITEMS);
      if (v != null) return v;
    }
    return "places";
  });
  const [fields, setFields] = useState(() => {
    if (urlFields) return urlFields;
    const hasRaw = readLS(LS_KEYS.RAW) != null;
    if (hasRaw) {
      const s = readLS(LS_KEYS.FIELDS);
      if (s) {
        try {
          const arr = JSON.parse(s);
          if (Array.isArray(arr) && arr.every((x) => typeof x === "string")) return arr;
        } catch (_) {}
      }
    }
    return ["displayName", "displayName.text"];
  });
  const [expandAll, setExpandAll] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Persist to cache: only store fields/items if JSON is also stored and not too large.
  useEffect(() => {
    const text = raw || "";
    if (!text.trim()) {
      removeLS(LS_KEYS.RAW);
      removeLS(LS_KEYS.ITEMS);
      removeLS(LS_KEYS.FIELDS);
      return;
    }
    if (text.length > MAX_CACHE_CHARS) {
      // Too big: don't cache anything to avoid large localStorage writes
      removeLS(LS_KEYS.RAW);
      removeLS(LS_KEYS.ITEMS);
      removeLS(LS_KEYS.FIELDS);
      return;
    }
    // Cache JSON and the associated selections
    writeLS(LS_KEYS.RAW, text);
    writeLS(LS_KEYS.ITEMS, itemsPath || "");
    writeLS(LS_KEYS.FIELDS, JSON.stringify(fields || []));
  }, [raw, itemsPath, fields]);

  // parse when raw changes
  useEffect(() => {
    if (!raw || !raw.trim()) {
      setData([null, null]);
      return;
    }
    const [parsed, err] = tryParseJSON(raw);
    setData([parsed, err]);
    // auto-detect itemsPath if possible
    if (parsed) {
      const { arrayPaths } = collectPaths(parsed);
      if (!itemsPath || !getArrayByPath(parsed, itemsPath).length) {
        // Choose first array path or root array
        const first = isArr(parsed) ? "" : Array.from(arrayPaths)[0] || "";
        setItemsPath(first);
      }
    }
  }, [raw]);

  const { paths, arrayPaths } = useMemo(() => {
    if (!data) return { paths: new Set(), arrayPaths: new Set() };
    return collectPaths(data);
  }, [data]);

  const pathSuggestions = useMemo(() => Array.from(paths).sort(), [paths]);
  const arrayPathSuggestions = useMemo(() => {
    const arrs = Array.from(arrayPaths);
    if (isArr(data)) arrs.unshift("");
    return arrs.sort();
  }, [arrayPaths, data]);

  const items = useMemo(() => (data ? getArrayByPath(data, itemsPath) : []), [data, itemsPath]);

  const rows = useMemo(() => {
    const relFields = fields.map((f) => {
      // allow both absolute (with itemsPath prefix) and relative
      const prefix = itemsPath ? itemsPath + "." : "";
      return f.startsWith(prefix) ? f.slice(prefix.length) : f;
    });
    return items.map((item, idx) => {
      const values = {};
      relFields.forEach((rf, i) => {
        const vals = getValuesAtPath(item, rf);
        values[fields[i]] = vals.map((v) => stringifyShort(v)).join(" | ");
      });
      return { idx, values };
    });
  }, [items, fields, itemsPath]);

  const faqs = useMemo(() => ([
    {
      q: "How do I extract a field for each item?",
      a: (
        <>
          Choose the array you want to iterate, then add a dot path such as <code>name</code> or <code>name.text</code>. The Results table shows the value for each row and supports CSV export.
        </>
      ),
    },
    {
      q: "How do I extract nested fields like name.text?",
      a: (
        <>
          Add the nested dot path (e.g., <code>name.text</code>). Arrays along the way are flattened so nested values appear correctly.
        </>
      ),
    },
    {
      q: "Does this upload my data?",
      a: (
        <>
          No. Everything runs locally in your browser for privacy. You can also download the processed CSV without any server round-trip.
        </>
      ),
    },
    {
      q: "Other things you can search for",
      a: (
        <>
          JSON viewer, JSON to CSV, JSON path extractor, JSON explorer, pick fields from JSON, parse JSON online, JSONPath alternative.
        </>
      ),
    },
  ]), []);

  const handleFile = async (f) => {
    if (!f) return;
    const text = await f.text();
    setRaw(text);
  };

  const downloadCSV = () => {
    const csv = toCSV(rows, fields);
    download("selected-fields.csv", csv);
  };

  const copyJSON = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    } catch (e) {}
  };

  const copyTable = async () => {
    const csv = toCSV(rows, fields);
    try {
      await navigator.clipboard.writeText(csv);
    } catch (e) {}
  };

  const dropRef = useRef(null);
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const onDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const f = e.dataTransfer.files?.[0];
      if (f) handleFile(f);
      el.classList.remove("ring-2", "ring-indigo-400");
    };
    const onDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      el.classList.add("ring-2", "ring-indigo-400");
    };
    const onDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      el.classList.remove("ring-2", "ring-indigo-400");
    };
    el.addEventListener("drop", onDrop);
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    return () => {
      el.removeEventListener("drop", onDrop);
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
    };
  }, []);

  return (
    <HelmetProvider>
      <>
        <Helmet>
          <title>{SEO.title}</title>
          <meta name="description" content={SEO.description} />
          <meta name="keywords" content={SEO.keywords} />
          <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
          <link rel="canonical" href={SEO.url} />
          <meta property="og:type" content="website" />
          <meta property="og:site_name" content="JSON Field Explorer" />
          <meta property="og:title" content={SEO.title} />
          <meta property="og:description" content={SEO.description} />
          <meta property="og:url" content={SEO.url} />
          <meta property="og:image" content={SEO.image} />
          <meta property="og:image:alt" content="JSON Field Explorer UI preview" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={SEO.title} />
          <meta name="twitter:description" content={SEO.description} />
          <meta name="twitter:image" content={SEO.image} />
          <meta name="twitter:image:alt" content="JSON Field Explorer UI preview" />
          <link rel="icon" href="/favicon.ico" />
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "JSON Field Explorer",
            url: SEO.url,
            description: SEO.description,
            applicationCategory: "DeveloperApplication",
            operatingSystem: "Web",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" }
          })}</script>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "HowTo",
            "name": "Extract fields from JSON online",
            "step": [
              { "@type": "HowToStep", "name": "Paste JSON", "text": "Paste or drop a .json file." },
              { "@type": "HowToStep", "name": "Choose items array", "text": "Select the array path to iterate (e.g., places)." },
              { "@type": "HowToStep", "name": "Add dot paths", "text": "Enter fields such as name or name.text. Arrays are flattened." },
              { "@type": "HowToStep", "name": "Export CSV", "text": "Preview values per item and export a CSV." }
            ]
          })}</script>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              { "@type": "Question", name: "How do I extract a field from each item?", acceptedAnswer: { "@type": "Answer", text: "Choose the list (array) you want to iterate, then add a dot path for the field you want (e.g., name or name.text). The table shows the value per item and supports CSV export." } },
              { "@type": "Question", name: "Can I extract nested fields like name.text?", acceptedAnswer: { "@type": "Answer", text: "Yes. Add nested dot paths such as name.text. The app flattens arrays along the path so nested values appear correctly." } },
              { "@type": "Question", name: "Is my JSON uploaded anywhere?", acceptedAnswer: { "@type": "Answer", text: "No—everything runs in your browser for privacy. Paste or drag-and-drop JSON; nothing is sent to a server." } }
            ]
          })}</script>
        </Helmet>

        <div className="min-h-[100dvh] bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 text-slate-900 dark:text-slate-100">
          <noscript>
            <div class="max-w-6xl mx-auto px-4 py-3 text-sm">
              JSON Field Explorer — free online JSON viewer & JSON path extractor. Paste JSON, choose an items array, add dot paths (e.g. <span class="font-mono">name.text</span>), preview values per item, and export CSV. Private, in‑browser.
            </div>
          </noscript>

          <header className="sticky top-0 z-10 backdrop-blur bg-white/60 dark:bg-slate-950/60 border-b border-slate-200/60 dark:border-slate-800/60">
            <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-2xl bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 grid place-items-center shadow">
                <Wand2 size={18} />
              </div>
              <div>
                <h1 className="text-lg font-semibold leading-tight">JSON Field Explorer — Free Online JSON Viewer &amp; JSON Path Extractor</h1>
                <p className="text-xs text-slate-500">Load JSON → pick an items array → select fields (e.g. <span className="font-mono">name.text</span>) → see values per item → export CSV.</p>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button onClick={copyJSON} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-xl bg-slate-200/60 dark:bg-slate-800/60 hover:bg-slate-300/60 dark:hover:bg-slate-700/60">
                  <Copy size={14} /> Copy JSON
                </button>
                <button onClick={() => download("data.json", JSON.stringify(data, null, 2))} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-xl bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
                  <Download size={14} /> Download JSON
                </button>
                <a href="#faq" className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700">
                  <HelpCircle size={14} /> Q&amp;A
                </a>
              </div>
            </div>
          </header>

          <main className="max-w-6xl mx-auto px-4 py-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Left column: loader + options */}
            <section className="lg:col-span-1 space-y-4">
              <div ref={dropRef} className="rounded-2xl border border-dashed border-slate-300 dark:border-slate-700 p-4 bg-white/70 dark:bg-slate-900/70">
                <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300">
                  <Upload size={16} />
                  <h2 className="text-sm font-semibold">Load JSON</h2>
                </div>
                <p className="text-xs text-slate-500 mt-1">Drag & drop a .json file here, paste into the box, or use the sample.</p>
                <div className="mt-3 flex items-center gap-2">
                  <label className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded-xl bg-slate-200/60 dark:bg-slate-800/60 cursor-pointer">
                    <FolderOpen size={14} />
                    <span>Choose file…</span>
                    <input type="file" accept="application/json,.json" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                  </label>
                  <button onClick={() => setRaw(JSON.stringify(defaultSample, null, 2))} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-xl bg-slate-200/60 dark:bg-slate-800/60">
                    Use sample
                  </button>
                  <button onClick={() => { setRaw(""); }} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-xl text-rose-600 bg-rose-50 dark:bg-rose-950/30">
                    <Trash2 size={14} /> Clear
                  </button>
                </div>
                <textarea
                  value={raw}
                  onChange={(e) => setRaw(e.target.value)}
                  spellCheck={false}
                  className="mt-3 w-full h-48 rounded-xl border border-slate-200 dark:border-slate-800 bg-white/70 dark:bg-slate-900/70 p-3 font-mono text-xs text-slate-800 dark:text-slate-200"
                  placeholder="Paste your JSON here"
                />
                {raw.trim() && parseErr && (
                  <div className="mt-2 text-xs text-rose-600">Parse error: {parseErr}</div>
                )}
              </div>

              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white/70 dark:bg-slate-900/70 space-y-3">
                <div className="text-sm font-semibold">1) Choose the list to iterate</div>
                <div className="text-xs text-slate-500">Pick the array path that represents your items (e.g., <span className="font-mono">places</span>).</div>
                <div className="flex gap-2">
                  <input
                    value={itemsPath}
                    onChange={(e) => setItemsPath(e.target.value)}
                    list="arrayPaths"
                    placeholder="Array path (e.g. places)"
                    className="flex-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-900/70 px-3 py-2 text-sm"
                  />
                  <datalist id="arrayPaths">
                    {arrayPathSuggestions.map((p) => (
                      <option key={p || "__root"} value={p} />
                    ))}
                  </datalist>
                </div>
                <div className="flex justify-between text-xs text-slate-500"><span>Detected arrays: {arrayPathSuggestions.length || 0}</span><span>Items found: {items.length}</span></div>
              </div>

              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white/70 dark:bg-slate-900/70 space-y-3">
                <div className="text-sm font-semibold">2) Pick fields to show</div>
                <div className="text-xs text-slate-500">Use dot paths. You can include or omit the list prefix (<span className="font-mono">{itemsPath}</span>).</div>
                <TagsInput suggestions={pathSuggestions} value={fields} onChange={setFields} />
              </div>

              
              </section>

              {/* Right: results table */}
              <section className="lg:col-span-2 space-y-3">
                <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white/80 dark:bg-slate-900/80">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold">Results</h2>
                  <span className="text-xs text-slate-500">{rows.length} item{rows.length === 1 ? "" : "s"}</span>
                  <div className="ml-auto flex items-center gap-2">
                    <button onClick={copyTable} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-xl bg-slate-200/60 dark:bg-slate-800/60">
                      <Copy size={14} /> Copy CSV
                    </button>
                    <button onClick={downloadCSV} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-xl bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900">
                      <Download size={14} /> Export CSV
                    </button>
                  </div>
                </div>
                <div className="mt-3 overflow-auto border rounded-2xl border-slate-200 dark:border-slate-800">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-100/80 dark:bg-slate-800/60 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left w-16">#</th>
                        {fields.map((f) => (
                          <th key={f} className="px-3 py-2 text-left whitespace-nowrap font-medium">{f}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.idx} className="odd:bg-white even:bg-slate-50/50 dark:odd:bg-slate-900 dark:even:bg-slate-900/60">
                          <td className="px-3 py-2 text-slate-500 align-top">{r.idx}</td>
                          {fields.map((f) => (
                            <td key={f} className="px-3 py-2 align-top">
                              <div className="max-w-[40rem] whitespace-pre-wrap break-words text-slate-800 dark:text-slate-200">{r.values[f] || "—"}</div>
                            </td>
                          ))}
                        </tr>
                      ))}
                      {rows.length === 0 && (
                        <tr>
                          <td colSpan={fields.length + 1} className="px-3 py-8 text-center text-slate-500">No items. Pick a valid array path.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 text-xs text-slate-500">Tip: Try fields like <span className="font-mono">name</span> or <span className="font-mono">name.text</span> after choosing your list path.</div>
              </div>

              {/* Wide JSON preview */}
              <div className="rounded-2xl border border-slate-200 dark:border-slate-800 p-4 bg-white/80 dark:bg-slate-900/80">
                <div className="flex items-center gap-2">
                  <h2 className="text-sm font-semibold">JSON Preview</h2>
                  <span className="text-xs text-slate-500">Compact tree</span>
                  <div className="ml-auto">
                    <button onClick={() => setExpandAll((s) => !s)} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-xl bg-slate-200/60 dark:bg-slate-800/60">
                      {expandAll ? "Collapse all" : "Expand all"}
                    </button>
                  </div>
                </div>
                <div className="mt-2 min-h-40 max-h-[60vh] md:max-h-[70vh] overflow-auto rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-950/40 p-3 text-sm">
                  {data ? (
                    <TreeNode v={data} defaultOpen={expandAll} />
                  ) : (
                    <div className="text-xs text-slate-500">No JSON loaded. Paste JSON to preview.</div>
                  )}
                </div>
              </div>
            </section>
          </main>

          <section id="faq" className="max-w-6xl mx-auto px-4 py-8">
            <div className="mb-3 flex items-center gap-2 text-slate-800 dark:text-slate-200">
              <HelpCircle size={16} />
              <h2 className="text-base font-semibold">Q&amp;A</h2>
              <span className="text-xs text-slate-500">Common questions</span>
            </div>
            <div className="space-y-2">
              {faqs.map((item, i) => (
                <div key={i} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white/80 dark:bg-slate-900/80 overflow-hidden">
                  <button
                    onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50/60 dark:hover:bg-slate-800/40"
                    aria-expanded={openFaqIndex === i}
                    aria-controls={`faq-panel-${i}`}
                    id={`faq-button-${i}`}
                  >
                    <span className="text-sm font-medium pr-4">{item.q}</span>
                    {openFaqIndex === i ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>
                  {openFaqIndex === i && (
                    <div id={`faq-panel-${i}`} role="region" aria-labelledby={`faq-button-${i}`} className="px-4 pb-4 text-sm text-slate-600 dark:text-slate-300">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>

          <footer className="max-w-6xl mx-auto px-4 py-6 text-xs text-slate-500">
            <div className="flex flex-wrap gap-3">
              <span>Built with React + Tailwind. Drop in any JSON—no servers.</span>
              <a href="/json-path-extractor/" className="underline">JSON Path Extractor</a>
              <a href="/extract-fields-from-json-online/" className="underline">Extract fields from JSON online</a>
            </div>
            <div className="mt-2">For arrays nested deeper than 3 levels for suggestions, just type the dot path manually.</div>
          </footer>
        </div>
      </>
    </HelmetProvider>
  );
}
